/**
 * Строка пакета в журнале (§8): 36 px, раскрывается в тот же дифф, что на ревью.
 *
 * Строка умышленно не кнопка целиком: внутри живут три независимых действия —
 * раскрыть пакет, уйти в фильтр по автору и открыть пакет отдельной страницей.
 * Раскрытие вынесено в треугольник слева, у него есть `aria-expanded`.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ChangeSetDetail, ChangeSetSummary, ChannelCode } from '@shared/types.ts'
import {
  Avatar, Badge, Button, IconChevronDown, IconChevronRight, IconCheck, IconWarning,
  IconButton, Menu, Skeleton, Tooltip,
} from '../../components/index.js'
import type { MenuItem } from '../../components/index.js'
import { useApp } from '../../store/app-store.js'
import { channelFallbackNames, historyTexts, formatTableWhen, formatRelative } from './texts.history.js'
import { ChangeDiff } from './ChangeDiff.js'
import './ChangeSetRow.css'

export interface ChangeSetRowProps {
  item: ChangeSetSummary
  /** Сколько колонок в таблице: строка с диффом растягивается на все. */
  columns: number
  expanded: boolean
  onToggle: () => void
  detail: ChangeSetDetail | null
  detailState: 'idle' | 'loading' | 'error'
  detailError?: string | null
  /** Поиск по бренду: подсветка вхождений в раскрытом диффе (§8). */
  highlight?: string
  /** Канал из фильтра — когда дифф ещё не загружен, показываем хотя бы его. */
  fallbackChannel?: ChannelCode | null
  onPickAuthor: (authorId: number) => void
  canRevert: boolean
  onRevert: (item: ChangeSetSummary) => void
  canNotify: boolean
  notifying: boolean
  onNotifyAgain: (item: ChangeSetSummary) => void
}

/** Каналы, которых коснулся пакет: «Розница», «Опт» или «Розница, Опт». */
export function channelLabelOfChanges(detail: ChangeSetDetail | null): string | null {
  if (!detail) return null
  const codes = new Set<ChannelCode>()
  for (const change of detail.changes) {
    const code = change.key.channelCode
    if (code) codes.add(code)
  }
  if (codes.size === 0) return null
  return (['retail', 'b2b'] as ChannelCode[])
    .filter((code) => codes.has(code))
    .map((code) => channelFallbackNames[code])
    .join(', ')
}

function TelegramCell({
  item, canNotify, notifying, onNotifyAgain,
}: Pick<ChangeSetRowProps, 'item' | 'canNotify' | 'notifying' | 'onNotifyAgain'>) {
  if (item.isSystem) {
    return (
      <span className="hrow__dash" title={historyTexts.telegram.skipped}>
        —
        <span className="visually-hidden">{historyTexts.telegram.skipped}</span>
      </span>
    )
  }

  switch (item.telegramStatus) {
    case 'sent':
      return (
        <span className="hrow__tg hrow__tg--sent">
          <IconCheck />
          <span className="visually-hidden">{historyTexts.telegram.sent}</span>
        </span>
      )
    case 'pending':
      return <Badge variant="neutral" dot srLabel={historyTexts.telegram.pending}>{historyTexts.telegram.pending}</Badge>
    case 'failed':
      return (
        <span className="hrow__tg hrow__tg--failed">
          <IconWarning />
          {canNotify ? (
            <>
              <span className="visually-hidden">{historyTexts.telegram.failed}</span>
              <Button
                size="s"
                variant="ghost"
                loading={notifying}
                onClick={() => onNotifyAgain(item)}
              >
                {historyTexts.telegram.retry}
              </Button>
            </>
          ) : (
            /* Без права кнопки нет, но причина видна рядом (§11.4). */
            <Tooltip content={historyTexts.telegram.noRight}>
              <span className="hrow__tg-reason t-meta" tabIndex={0}>{historyTexts.telegram.failed}</span>
            </Tooltip>
          )}
        </span>
      )
    default:
      /* «Уведомления выключены» — подпись, а не действие: лишний таб-стоп на
         каждой из 50 строк дороже тултипа (§13). */
      return (
        <span className="hrow__dash" title={historyTexts.telegram.skipped}>
          —
          <span className="visually-hidden">{historyTexts.telegram.skipped}</span>
        </span>
      )
  }
}

export function ChangeSetRow({
  item, columns, expanded, onToggle, detail, detailState, detailError, highlight,
  fallbackChannel, onPickAuthor, canRevert, onRevert, canNotify, notifying, onNotifyAgain,
}: ChangeSetRowProps) {
  const navigate = useNavigate()
  const users = useApp((s) => s.bootstrap?.users ?? [])
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const author = users.find((user) => user.id === item.authorId)
  const authorName = item.isSystem
    ? historyTexts.systemAuthor
    : author?.shortName ?? historyTexts.unknownAuthor

  /* Канал берём из сводки пакета: подробности грузятся только при раскрытии,
     и до него колонка «Канал» стояла пустой у всех строк. */
  const channel = channelLabelOfChanges(detail)
    ?? (item.channels.length > 0
      ? item.channels.map((code) => channelFallbackNames[code]).join(', ')
      : null)
    ?? (fallbackChannel ? channelFallbackNames[fallbackChannel] : null)

  const revertBlockedReason = item.isSystem
    ? historyTexts.revert.systemDisabled
    : item.status === 'reverted'
      ? historyTexts.revert.alreadyReverted
      : !canRevert
        ? historyTexts.revert.noRight
        : null

  const menuItems: MenuItem[] = [
    { id: 'open', label: historyTexts.revert.openPack },
    {
      id: 'revert',
      label: historyTexts.revert.action,
      danger: true,
      disabled: revertBlockedReason !== null,
      reason: revertBlockedReason ?? undefined,
      separatorBefore: true,
    },
  ]
  if (item.telegramStatus === 'failed') {
    menuItems.push({
      id: 'notify',
      label: historyTexts.telegram.retry,
      disabled: !canNotify,
      reason: canNotify ? undefined : historyTexts.telegram.noRight,
    })
  }

  return (
    <>
      <tr className={expanded ? 'hrow hrow--open' : 'hrow'}>
        <td className="hrow__cell hrow__cell--id">
          <span className="hrow__id">
          <button
            type="button"
            className="hrow__toggle"
            aria-expanded={expanded}
            aria-controls={`changeset-diff-${item.id}`}
            aria-label={expanded ? historyTexts.collapseLabel(item.id) : historyTexts.expandLabel(item.id)}
            onClick={onToggle}
          >
            {expanded ? <IconChevronDown /> : <IconChevronRight />}
          </button>
          <Link className="hrow__number" to={`/history/${item.id}`}>#{item.id}</Link>
          </span>
        </td>

        <td className="hrow__cell hrow__cell--when">
          {/* Относительное время — в title: 50 строк на странице не должны
              добавить 50 остановок Tab (§13). */}
          <time className="hrow__when t-cell" dateTime={item.committedAt} title={formatRelative(item.committedAt)}>
            {formatTableWhen(item.committedAt)}
          </time>
        </td>

        <td className="hrow__cell hrow__cell--author">
          <button
            type="button"
            className="hrow__author"
            aria-label={historyTexts.authorFilterLabel(authorName)}
            onClick={() => onPickAuthor(item.authorId)}
          >
            <Avatar name={authorName} system={item.isSystem} />
            <span className="hrow__author-name t-cell">{authorName}</span>
          </button>
        </td>

        <td className="hrow__cell hrow__cell--note">
          <span className="hrow__note-wrap">
          <span className="hrow__note t-cell" title={item.note ?? undefined}>
            {item.note ?? historyTexts.noNote}
          </span>
          <span className="hrow__marks">
            {item.isSystem ? (
              <Tooltip content={historyTexts.marks.systemHint}>
                <span tabIndex={0}><Badge variant="neutral">{historyTexts.marks.system}</Badge></span>
              </Tooltip>
            ) : null}
            {item.revertsChangeSetId !== null ? (
              <Link className="hrow__mark-link" to={`/history/${item.revertsChangeSetId}`}>
                <Badge variant="accent">{historyTexts.marks.revertOf(item.revertsChangeSetId)}</Badge>
              </Link>
            ) : null}
            {item.revertedByChangeSetId !== null ? (
              <Link className="hrow__mark-link" to={`/history/${item.revertedByChangeSetId}`}>
                <Badge variant="neutral">{historyTexts.marks.revertedBy(item.revertedByChangeSetId)}</Badge>
              </Link>
            ) : null}
          </span>
          </span>
        </td>

        <td className="hrow__cell hrow__cell--channel t-cell">
          {channel ?? <span className="hrow__dash">—</span>}
        </td>

        <td className="hrow__cell hrow__cell--counts t-cell">
          <span aria-label={historyTexts.countsCellLabel(item.changesCount, item.brandsCount)}>
            {historyTexts.countsCell(item.changesCount, item.brandsCount)}
          </span>
        </td>

        <td className="hrow__cell hrow__cell--tg">
          <TelegramCell
            item={item}
            canNotify={canNotify}
            notifying={notifying}
            onNotifyAgain={onNotifyAgain}
          />
        </td>

        <td className="hrow__cell hrow__cell--actions">
          <IconButton
            ref={setMenuAnchor}
            inTable
            label={historyTexts.rowActionsLabel(item.id)}
            icon={<span aria-hidden="true">⋯</span>}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
          />
          <Menu
            open={menuOpen}
            anchor={menuAnchor}
            items={menuItems}
            label={historyTexts.rowActionsLabel(item.id)}
            onClose={() => setMenuOpen(false)}
            onSelect={(id) => {
              if (id === 'revert') onRevert(item)
              if (id === 'notify') onNotifyAgain(item)
              if (id === 'open') navigate(`/history/${item.id}`)
            }}
          />
        </td>
      </tr>

      {expanded ? (
        <tr className="hrow-detail">
          <td className="hrow-detail__cell" id={`changeset-diff-${item.id}`} colSpan={columns}>
            {detailState === 'loading' ? (
              <Skeleton rows={3} columns={[220, 120, 120]} label={historyTexts.loadingLabel} />
            ) : detailState === 'error' ? (
              <p className="hrow-detail__error t-cell">{detailError ?? historyTexts.errorTitle}</p>
            ) : detail ? (
              <ChangeDiff
                changes={detail.changes}
                highlight={highlight}
                channel={fallbackChannel ?? null}
              />
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  )
}
